import React, { useState, useRef, useEffect } from 'react';
import './ColumnSearchBar.css';

export interface SearchField {
  key: string;
  label: string;
}

export interface SearchChip {
  id: string;
  field: string;
  label: string;
  value: string;
}

interface ColumnSearchBarProps {
  fields: SearchField[];
  chips: SearchChip[];
  onChipsChange: (chips: SearchChip[]) => void;
  placeholder?: string;
}

const ColumnSearchBar: React.FC<ColumnSearchBarProps> = ({
  fields,
  chips,
  onChipsChange,
  placeholder = 'Search...',
}) => {
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const addChip = (field: SearchField) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    onChipsChange([
      ...chips,
      {
        id: Math.random().toString(36).slice(2, 11),
        field: field.key,
        label: field.label,
        value: trimmed,
      },
    ]);
    setQuery('');
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const removeChip = (chipId: string) => {
    onChipsChange(chips.filter((c) => c.id !== chipId));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim() && fields.length > 0) {
      e.preventDefault();
      addChip(fields[selectedIndex]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % fields.length);
      setShowDropdown(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + fields.length) % fields.length);
      setShowDropdown(true);
    } else if (e.key === 'Backspace' && !query && chips.length > 0) {
      removeChip(chips[chips.length - 1].id);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div
      className="csb-container"
      ref={containerRef}
      onClick={() => inputRef.current?.focus()}
    >
      <svg
        className="csb-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      {chips.map((chip) => (
        <span key={chip.id} className="csb-chip">
          <span className="csb-chip-field">{chip.label}</span>
          <span className="csb-chip-value">{chip.value}</span>
          <button
            className="csb-chip-x"
            onClick={(e) => {
              e.stopPropagation();
              removeChip(chip.id);
            }}
            title="Remove"
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        className="csb-input"
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowDropdown(e.target.value.trim().length > 0);
        }}
        onKeyDown={handleKeyDown}
        placeholder={chips.length > 0 ? 'Search...' : placeholder}
      />
      {showDropdown && query.trim() && (
        <div className="csb-dropdown">
          {fields.map((field, index) => (
            <div
              key={field.key}
              className={`csb-dropdown-item ${index === selectedIndex ? 'csb-dropdown-item-selected' : ''}`}
              onClick={() => addChip(field)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="csb-dropdown-arrow">▸</span>
              <span>
                Search <strong>{field.label}</strong> for:{' '}
                <em>{query.trim()}</em>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ColumnSearchBar;
